import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { scrollToBooking } from "../utils/scrollToSection";
import { Helmet } from "react-helmet";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  useTheme,
  useMediaQuery
} from "@mui/material";
import {
  ArrowForward as ArrowIcon,
  Event as EventIcon
} from "@mui/icons-material";
import { getAllServices } from "../data/servicesData";
import Navbar from "../components/navbar";
import Footer from "../components/footer";
import BookingForm from "../components/bookingForm";

// Service images mapped by slug
import weddingImg from "../assets/dhol-players-asian-wedding-reception.webp";
import corporateImg from "../assets/dhol-players-barclays-corporate-event.webp";
import festivalImg from "../assets/dhol-drummers-football-stadium-performance.webp";
import mediaImg from "../assets/dhol-players-bbc-asian-network-performance.webp";

const serviceImages = {
  "wedding-entertainment": weddingImg,
  "corporate-events": corporateImg,
  "cultural-festivals": festivalImg,
  "media-and-production": mediaImg
};

const ServicesPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const services = getAllServices();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <>
      <Helmet>
        <title>Our Services | Professional Dhol Entertainment | Imperial Drummers</title>
        <meta name="description" content="Explore Imperial Drummers' professional dhol entertainment services for weddings, corporate events, cultural festivals, and media productions. Available UK-wide and internationally." />
        <meta name="keywords" content="dhol services, wedding dhol, corporate dhol entertainment, festival dhol players, media production dhol, dhol hire UK" />
        <link rel="canonical" href="https://imperialdrummers.com/services" />

        <meta property="og:title" content="Our Services | Professional Dhol Entertainment | Imperial Drummers" />
        <meta property="og:description" content="Professional dhol entertainment for weddings, corporate events, cultural festivals, and media productions." />
        <meta property="og:url" content="https://imperialdrummers.com/services" />
        <meta property="og:type" content="website" />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Imperial Drummers Entertainment Services",
            "description": "Professional dhol entertainment for weddings, corporate events, cultural festivals, and media productions.",
            "url": "https://imperialdrummers.com/services",
            "provider": {
              "@type": "Organization",
              "name": "Imperial Drummers",
              "url": "https://imperialdrummers.com"
            },
            "hasOfferCatalog": {
              "@type": "OfferCatalog",
              "name": "Dhol Entertainment Services",
              "itemListElement": services.map((s, i) => ({
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": s.title,
                  "description": s.metaDescription,
                  "url": `https://imperialdrummers.com/services/${s.slug}`
                }
              }))
            }
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              {
                "@type": "ListItem",
                "position": 1,
                "name": "Home",
                "item": "https://imperialdrummers.com/"
              },
              {
                "@type": "ListItem",
                "position": 2,
                "name": "Services",
                "item": "https://imperialdrummers.com/services"
              }
            ]
          })}
        </script>
      </Helmet>

      <Navbar />

      <Box component="main">
        {/* Hero Section */}
        <Box sx={{
          position: 'relative',
          minHeight: { xs: '60vh', md: '70vh' },
          width: '100%',
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #5C005D 0%, #1a0a2e 100%)',
          color: 'white',
          pt: { xs: 12, md: 15 },
          pb: { xs: 8, md: 10 },
        }}>
          {/* Sophisticated Overlay System */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.2) 50%, rgba(0,0,0,0.5) 100%)',
            zIndex: 1
          }} />

          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'radial-gradient(circle at 20% 50%, rgba(255, 215, 0, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(138, 43, 226, 0.2) 0%, transparent 50%)',
            pointerEvents: 'none',
            zIndex: 1
          }} />

          {/* Vignette effect */}
          <Box sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            boxShadow: 'inset 0 0 150px rgba(0,0,0,0.5)',
            zIndex: 1,
            pointerEvents: 'none'
          }} />

          <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
            <Box sx={{ textAlign: 'center', maxWidth: 800, mx: 'auto' }}>
              <Box className="hero-stagger hero-stagger-up" sx={{ animationDuration: '0.5s', animationDelay: '0s' }}>
                <Chip
                  label="Our Services"
                  sx={{
                    mb: 3,
                    backgroundColor: 'rgba(255, 215, 0, 0.15)',
                    color: '#ffd700',
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    letterSpacing: '2px',
                    textTransform: 'uppercase',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255, 215, 0, 0.3)'
                  }}
                />
              </Box>

              <Box className="hero-stagger hero-stagger-up" sx={{ animationDuration: '0.5s', animationDelay: '0.1s' }}>
                <Typography
                  variant={isMobile ? "h4" : "h2"}
                  component="h1"
                  sx={{
                    fontWeight: 800,
                    mb: 3,
                    textShadow: '0 4px 12px rgba(0,0,0,0.4)',
                    fontFamily: '"EB Garamond SC", serif',
                    letterSpacing: '-0.5px',
                    fontSize: { md: '3.5rem' }
                  }}
                >
                  Professional <span style={{ color: '#ffd700' }}>Dhol</span> Entertainment
                </Typography>
              </Box>

              <Box className="hero-stagger hero-stagger-up" sx={{ animationDuration: '0.5s', animationDelay: '0.2s' }}>
                <Typography
                  variant="h5"
                  sx={{
                    mb: 5,
                    opacity: 0.9,
                    lineHeight: 1.7,
                    fontWeight: 400,
                    fontSize: { xs: '1.1rem', md: '1.25rem' },
                    textShadow: '0 2px 4px rgba(0,0,0,0.3)'
                  }}
                >
                  From grand wedding entrances to corporate showcases, cultural festivals, and media productions
                  - we bring the authentic energy of live dhol to every occasion.
                </Typography>
              </Box>

              <Box className="hero-stagger hero-stagger-up" sx={{ animationDuration: '0.5s', animationDelay: '0.3s' }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={scrollToBooking}
                  startIcon={<EventIcon />}
                  color="gold"
                  sx={{
                    px: 6,
                    py: 2,
                    boxShadow: '0 8px 25px rgba(255, 215, 0, 0.3)',
                    '&:hover': {
                      boxShadow: '0 12px 30px rgba(255, 215, 0, 0.4)',
                    }
                  }}
                >
                  Get Free Quote
                </Button>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* Services Grid */}
        <Box sx={{ py: { xs: 6, md: 10 }, backgroundColor: '#f8f5fb' }}>
          <Container maxWidth="lg">
            <Typography variant="h2" align="center" sx={{ color: '#5C005D', mb: 2, fontSize: { xs: '2rem', md: '3rem' } }}>
              What We Do
            </Typography>
            <Typography variant="h6" align="center" sx={{ color: '#666', mb: 6, maxWidth: 700, mx: 'auto' }}>
              Explore our range of professional dhol entertainment services, tailored to make every event unforgettable
            </Typography>

            <Grid container spacing={4}>
              {services.map((service) => (
                <Grid item xs={12} sm={6} key={service.slug}>
                  <Card
                    component={Link}
                    to={`/services/${service.slug}`}
                    sx={{
                      textDecoration: 'none',
                      display: 'flex',
                      flexDirection: 'column',
                      height: '100%',
                      overflow: 'hidden',
                      transition: 'all 0.4s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 16px 40px rgba(92, 0, 93, 0.25)',
                      },
                      '&:hover .service-image': {
                        transform: 'scale(1.08)',
                      },
                      '&:hover .arrow-icon': {
                        transform: 'translateX(4px)',
                      }
                    }}
                  >
                    <Box sx={{ position: 'relative', overflow: 'hidden' }}>
                      <CardMedia
                        className="service-image"
                        component="img"
                        height={isMobile ? "220" : "280"}
                        image={serviceImages[service.slug]}
                        alt={service.title}
                        loading="lazy"
                        sx={{
                          transition: 'transform 0.6s ease',
                        }}
                      />
                      <Box sx={{
                        position: 'absolute',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        height: '50%',
                        background: 'linear-gradient(to top, rgba(0,0,0,0.6), transparent)',
                        pointerEvents: 'none'
                      }} />
                      <Chip
                        label={service.hero.badge}
                        sx={{
                          position: 'absolute',
                          top: 16,
                          left: 16,
                          backgroundColor: 'rgba(255, 215, 0, 0.9)',
                          color: '#1a0a2e',
                          fontWeight: 'bold',
                          fontSize: '0.8rem',
                          letterSpacing: '0.5px'
                        }}
                      />
                    </Box>

                    <CardContent sx={{ p: 3, flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                      <Typography variant="h5" sx={{ color: '#5C005D', mb: 1.5, fontWeight: 'bold' }}>
                        {service.title}
                      </Typography>
                      <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7, mb: 2, flexGrow: 1 }}>
                        {service.hero.subheading}
                      </Typography>

                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                        {service.services.slice(0, 3).map((s, i) => (
                          <Chip
                            key={i}
                            label={s.title}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: 'rgba(92, 0, 93, 0.3)',
                              color: '#5C005D',
                              fontSize: '0.75rem'
                            }}
                          />
                        ))}
                      </Box>

                      <Box sx={{ display: 'flex', alignItems: 'center', color: '#5C005D', mt: 'auto' }}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          Learn More
                        </Typography>
                        <ArrowIcon className="arrow-icon" sx={{ fontSize: 18, ml: 0.5, transition: 'transform 0.3s ease' }} />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Container>
        </Box>

        {/* CTA Section */}
        <Box sx={{
          py: 8,
          background: 'linear-gradient(135deg, #5C005D 0%, #1a0a2e 100%)',
          color: 'white',
          textAlign: 'center'
        }}>
          <Container maxWidth="md">
            <Typography variant="h3" sx={{ mb: 2, fontWeight: 'bold', fontSize: { xs: '1.75rem', md: '2.5rem' } }}>
              Not Sure Which Service You Need?
            </Typography>
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9, lineHeight: 1.7 }}>
              Tell us about your event and we'll recommend the perfect package. Every celebration is unique, and we tailor our performances to match your vision.
            </Typography>
            <Button
              variant="contained"
              size="large"
              onClick={scrollToBooking}
              startIcon={<EventIcon />}
              color="gold"
              sx={{
                px: 5,
                py: 1.5,
                boxShadow: '0 8px 20px rgba(255, 215, 0, 0.3)',
              }}
            >
              Get a Free Quote
            </Button>
          </Container>
        </Box>

        {/* Booking Form */}
        <Box sx={{ py: 8, backgroundColor: '#f8f5fb' }}>
          <BookingForm />
        </Box>

        <Footer />
      </Box>
    </>
  );
};

export default ServicesPage;
